import { useMutation } from '@tanstack/react-query'
import {
  ApiPostEmployeeAbsence,
  ApiPutEmployeeAbsence,
  ViewEmployeeAbsence,
  ViewPostEmployeeAbsence,
  ViewPutEmployeeAbsence,
} from '@xeetr/types/models'
import { formatInTimeZone } from 'date-fns-tz'
import { useContext, useMemo } from 'react'

import { ActiveTenantContext } from '../context/common/Context'
import { invalidateBookingQueries, invalidateTenantQueries } from '../lib/mutationHelpers'
import { EmployeeAbsenceService } from '../services/EmployeeAbsence.service'

import { useTenantRequiredService } from './common/useServiceFactory'
import { useEmployees } from './useEmployees'



import { showErrorToastUnlessPermissionDenied, showToast } from '@/utils/toastMessage'

export interface AbsenceWithEmployee extends ViewEmployeeAbsence {
  employeeId: string
  employeeName: string
}

interface UseAbsencesReturn {
  absences: AbsenceWithEmployee[]
  
  create: (absence: ViewPostEmployeeAbsence, callback?: () => void) => void
  update: (absence: ViewPutEmployeeAbsence, callback?: () => void) => void
  remove: (absenceId: string, callback?: () => void) => void

  isCreating: boolean
  isUpdating: boolean
  isDeleting: boolean
}

export const useAbsences = (): UseAbsencesReturn => {
  const { tenantId, tenant } = useContext(ActiveTenantContext)
  const { employees } = useEmployees()

  // Extract and enrich absences from employees data with proper typing
  const absences = useMemo<AbsenceWithEmployee[]>(() => {
    if (!employees.length) return []
    return employees.flatMap(employee =>
      (employee.absences || []).map(absence => ({
        ...absence,
        employeeId: employee.id,
        employeeName: employee.name,
      }))
    )
  }, [employees])

  const getService = useTenantRequiredService(EmployeeAbsenceService)

  const createMutation = useMutation({
    mutationFn: async (employeeAbsence: ViewPostEmployeeAbsence) => {
      if (!tenant) throw new Error('Tenant information is required')
      const service = await getService()
      const formattedData: ApiPostEmployeeAbsence = {
        ...employeeAbsence,
        from: formatInTimeZone(employeeAbsence.from, tenant.timezone, 'yyyy-MM-dd HH:mm:ss'),
        until: formatInTimeZone(employeeAbsence.until, tenant.timezone, 'yyyy-MM-dd HH:mm:ss'),
      }
      return service.postEmployeeAbsence(formattedData)
    },
    onSuccess: () => {
      if (!tenantId) return

      invalidateTenantQueries(tenantId)
      invalidateBookingQueries(tenantId)

      showToast({ severity: 'success', detail: 'Die Abwesenheit wurde erfolgreich erstellt!' })
    },
    onError: (error) => {
      showErrorToastUnlessPermissionDenied(error, { severity: 'error' })
    },
  })

  const updateMutation = useMutation({
    mutationFn: async (employeeAbsence: ViewPutEmployeeAbsence) => {
      if (!tenant) throw new Error('Tenant information is required')
      const service = await getService()
      const formattedData: ApiPutEmployeeAbsence = {
        ...employeeAbsence,
        from: formatInTimeZone(employeeAbsence.from, tenant.timezone, 'yyyy-MM-dd HH:mm:ss'),
        until: formatInTimeZone(employeeAbsence.until, tenant.timezone, 'yyyy-MM-dd HH:mm:ss'),
      }
      return service.putEmployeeAbsence(formattedData)
    },
    onSuccess: () => {
      if (!tenantId) return

      invalidateTenantQueries(tenantId)
      invalidateBookingQueries(tenantId)

      showToast({ severity: 'success', detail: 'Die Abwesenheit wurde erfolgreich aktualisiert!' })
    },
    onError: (error) => {
      showErrorToastUnlessPermissionDenied(error, { severity: 'error' })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (absenceId: string) => {
      const service = await getService()
      return service.deleteEmployeeAbsence(absenceId)
    },
    onSuccess: () => {
      if (!tenantId) return

      invalidateTenantQueries(tenantId)
      invalidateBookingQueries(tenantId)

      showToast({ severity: 'success', detail: 'Die Abwesenheit wurde erfolgreich gelöscht!' })
    },
    onError: (error) => {
      showErrorToastUnlessPermissionDenied(error, { severity: 'error' })
    },
  })

  return {
    absences,
    
    create: (absence, callback) => {
      createMutation.mutate(absence, { onSuccess: callback })
    },
    update: (absence, callback) => {
      updateMutation.mutate(absence, { onSuccess: callback })
    },
    remove: (absenceId, callback) => {
      deleteMutation.mutate(absenceId, { onSuccess: callback })
    },

    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  }
}
